import type { CSSProperties, FC, ReactNode } from 'react'
import type { LayoutId } from '@/components/landing/mockups/layout-screen'

import { useEffect, useState } from 'react'
import {
    Columns2,
    Grid2x2,
    Grid3x3,
    LayoutGrid,
    Pause,
    Play
} from 'lucide-react'
import { cn } from '@/lib/utils'
import ExpandView from '@/components/generals/expand-view'
import PhoneFrame from '@/components/landing/mockups/phone-frame'
import LayoutScreen, { LAYOUT_META } from '@/components/landing/mockups/layout-screen'

const AUTO_DURATION = 4500
const ORDER: LayoutId[] = ['two', 'four', 'twelve', 'manga']

const MangaIcon: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' className={className}>
            <rect x='3' y='3' width='18' height='18' rx='2' />
            <path d='M14 3 10 21' />
            <path d='M3 13.5 21 10' />
        </svg>
    )
}

interface LayoutTab {
    id: LayoutId
    count: string
    word?: string
    icon: ReactNode
    pro?: boolean
}

const TABS: LayoutTab[] = [
    { id: 'two', count: '2', word: 'videos', icon: <Columns2 className='size-4' /> },
    { id: 'four', count: '4', word: 'videos', icon: <Grid2x2 className='size-4' /> },
    { id: 'twelve', count: '12', icon: <Grid3x3 className='size-4' />, pro: true },
    { id: 'manga', count: 'Manga', icon: <MangaIcon className='size-4' /> }
]

const LayoutDemo: FC = (): ReactNode => {
    const [layout, setLayout] = useState<LayoutId>('twelve')
    const [auto, setAuto] = useState(true)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setAuto(false)
    }, [])

    const advance = () => {
        setLayout((current) => ORDER[(ORDER.indexOf(current) + 1) % ORDER.length])
    }

    const select = (id: LayoutId) => {
        setLayout(id)
        setAuto(false)
    }

    const meta = LAYOUT_META[layout]

    return (
        <div
            id='demo'
            className='group/demo relative scroll-mt-24 overflow-hidden rounded-2xl border border-border bg-card shadow-[0_30px_70px_-35px_rgb(15_23_42/0.45)]'
        >
            <div className='flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5'>
                <div className='flex min-w-0 items-center gap-2.5'>
                    <LayoutGrid className='size-4 shrink-0 text-brand' />

                    <span className='font-heading text-sm font-bold text-foreground'>
                        MultiScreen
                    </span>

                    <span className='hidden truncate font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground sm:inline'>
                        Illustrative view
                    </span>
                </div>

                <span className='shrink-0 font-mono text-[0.65rem] font-medium uppercase tracking-[0.12em] text-brand'>
                    {meta.status}
                </span>
            </div>

            <div className='flex items-center justify-between gap-3 px-4 py-2.5 sm:px-5'>
                <span className='text-xs font-medium text-muted-foreground'>
                    Choose a layout
                </span>

                <div className='flex items-center gap-3'>
                    <button
                        type='button'
                        onClick={() => setAuto((value) => !value)}
                        aria-pressed={auto}
                        aria-label={auto ? 'Pause automatic layout changes' : 'Play automatic layout changes'}
                        className={cn(
                            'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.12em] transition-colors',
                            auto
                                ? 'border-brand/50 bg-accent text-brand'
                                : 'border-border bg-card text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {auto ? <Pause className='size-3' fill='currentColor' /> : <Play className='size-3' fill='currentColor' />}
                        Auto
                    </button>

                    <span className='hidden font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground sm:inline'>
                        Videos + photos
                    </span>
                </div>
            </div>

            <div className='relative overflow-hidden border-y border-border bg-stage px-[7%] pb-[10%] pt-[6%]'>
                <div role='img' aria-label={meta.description}>
                    <PhoneFrame>
                        <LayoutScreen layout={layout} motion eager />
                    </PhoneFrame>
                </div>

                <ExpandView
                    title={meta.title}
                    caption='Illustrative view. The interface may vary by device.'
                    className='absolute bottom-3 right-3'
                >
                    <PhoneFrame>
                        <LayoutScreen layout={layout} motion />
                    </PhoneFrame>
                </ExpandView>
            </div>

            <div role='group' aria-label='Choose a layout' className='grid grid-cols-4 gap-2 px-3 pt-3 sm:px-4 sm:pt-4'>
                {TABS.map((tab) => {
                    const active = tab.id === layout

                    return (
                        <button
                            key={tab.id}
                            type='button'
                            onClick={() => select(tab.id)}
                            aria-pressed={active}
                            className={cn(
                                'relative flex h-11 items-center justify-center gap-1.5 overflow-hidden rounded-lg border text-[0.8rem] font-semibold transition-colors sm:gap-2 sm:text-sm',
                                active
                                    ? 'border-primary bg-primary text-primary-foreground shadow-[0_10px_22px_-12px_rgb(37_99_235/0.9)]'
                                    : 'border-border bg-muted/60 text-foreground/80 hover:bg-muted hover:text-foreground'
                            )}
                        >
                            {tab.icon}

                            <span>
                                {tab.count}

                                {tab.word && (
                                    <span className='hidden sm:inline'>
                                        {` ${tab.word}`}
                                    </span>
                                )}
                            </span>

                            {tab.pro && (
                                <span className='rounded-[4px] bg-amber-400 px-1 py-px text-[0.55rem] font-extrabold leading-none tracking-wide text-amber-950'>
                                    PRO
                                </span>
                            )}

                            {active && auto && (
                                <span
                                    key={layout}
                                    aria-hidden='true'
                                    onAnimationEnd={advance}
                                    style={{ '--progress-duration': `${AUTO_DURATION}ms` } as CSSProperties}
                                    className='progress-fill absolute inset-x-0 bottom-0 h-[3px] bg-white/75 group-hover/demo:[animation-play-state:paused]'
                                />
                            )}
                        </button>
                    )
                })}
            </div>

            <p className='px-4 pb-4 pt-3 text-center text-[0.68rem] leading-relaxed text-muted-foreground'>
                Illustrative views of the features · The interface may vary by device.
            </p>
        </div>
    )
}

export default LayoutDemo
